import React, { useEffect, useRef } from "react";

// Format date nicely e.g. "01 April 2024"
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return String(dateStr);
  }
};

// Base Modal Container
const ModalContainer = ({
  isOpen,
  onClose,
  title,
  width = 540,
  children,
  onAdd,
  editMode,
  setEditMode,
  onSave,
  onCancel,
  records = [],
  currentIndex = 0,
  onNavigate,
  currentDate,
}) => {
  const modalDialogRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Auto-focus first field when modal opens or editMode activates
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      if (!bodyRef.current) return;
      const firstInput = bodyRef.current.querySelector(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled])'
      );
      if (firstInput) {
        firstInput.focus();
        if (typeof firstInput.select === "function" && firstInput.type !== "checkbox" && firstInput.type !== "radio") {
          try { firstInput.select(); } catch {}
        }
      }
    }, 120);
    return () => clearTimeout(timer);
  }, [isOpen, editMode]);

  // Handle Enter key navigation across all fields in the modal
  const handleModalKeyDown = (e) => {
    if (e.key === "Enter") {
      const active = document.activeElement;
      if (!active) return;

      // Allow Enter to naturally trigger button clicks (e.g. Save, Add, Cancel, Exit)
      if (active.tagName === "BUTTON") {
        return;
      }

      // Check if current active element is inside the modal form body
      if (bodyRef.current && bodyRef.current.contains(active)) {
        e.preventDefault();
        e.stopPropagation();

        const focusables = Array.from(
          bodyRef.current.querySelectorAll(
            'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])'
          )
        ).filter((el) => {
          const style = window.getComputedStyle(el);
          return (
            style.display !== "none" &&
            style.visibility !== "hidden" &&
            el.offsetWidth > 0 &&
            el.offsetHeight > 0
          );
        });

        const idx = focusables.indexOf(active);

        if (e.shiftKey) {
          // Shift + Enter: Move to previous field
          if (idx > 0) {
            const prevEl = focusables[idx - 1];
            prevEl.focus();
            if (typeof prevEl.scrollIntoView === "function") {
              try { prevEl.scrollIntoView({ block: "nearest", behavior: "smooth" }); } catch {}
            }
            if (typeof prevEl.select === "function" && prevEl.type !== "checkbox" && prevEl.type !== "radio") {
              try { prevEl.select(); } catch {}
            }
          }
          return;
        }

        // Enter: Move to next field
        if (idx >= 0 && idx < focusables.length - 1) {
          const nextEl = focusables[idx + 1];
          nextEl.focus();
          if (typeof nextEl.scrollIntoView === "function") {
            try { nextEl.scrollIntoView({ block: "nearest", behavior: "smooth" }); } catch {}
          }
          if (typeof nextEl.select === "function" && nextEl.type !== "checkbox" && nextEl.type !== "radio") {
            try { nextEl.select(); } catch {}
          }
        } else if (idx === focusables.length - 1) {
          // Reached the last field: focus Save button (in editMode) or Edit / default action button
          const actionBtn = modalDialogRef.current?.querySelector("[data-modal-save]") ||
            modalDialogRef.current?.querySelector("[data-modal-edit]") ||
            modalDialogRef.current?.querySelector("button:not([disabled])");
          if (actionBtn) {
            actionBtn.focus();
          }
        }
      }
    }
  };

  if (!isOpen) return null;

  const total = records ? records.length : 0;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        backdropFilter: "blur(3px)",
        WebkitBackdropFilter: "blur(3px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={modalDialogRef}
        data-modal-container
        className="modal-content"
        onKeyDown={handleModalKeyDown}
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          width: width,
          maxWidth: "96vw",
          fontSize: 12,
          boxShadow:
            "0 20px 30px -5px rgba(0, 0, 0, 0.2), 0 10px 12px -5px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <style>{`
          [data-modal-container] input:focus,
          [data-modal-container] select:focus {
            border-color: #2563eb !important;
            box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2) !important;
            outline: none !important;
          }
          [data-modal-container] input[type="checkbox"]:focus {
            outline: 2px solid #2563eb !important;
            outline-offset: 1px !important;
          }
        `}</style>

        {/* Title bar */}
        <div
          style={{
            background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
            color: "#ffffff",
            padding: "10px 16px",
            fontWeight: 600,
            fontSize: 13.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{title}</span>
          <span
            onClick={onClose}
            style={{
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: 15,
              opacity: 0.8,
              transition: "opacity 0.15s",
              lineHeight: 1,
            }}
            onMouseEnter={(e) => (e.target.style.opacity = 1)}
            onMouseLeave={(e) => (e.target.style.opacity = 0.8)}
            title="Close (ESC)"
          >
            ✕
          </span>
        </div>

        {/* Form Body */}
        <div
          ref={bodyRef}
          style={{
            padding: "14px 18px",
            maxHeight: "68vh",
            overflowY: "auto",
          }}
        >
          {children}
        </div>

        {/* Action Buttons & Record Navigation Footer */}
        <div
          style={{
            borderTop: "1px solid #e2e8f0",
            padding: "10px 16px",
            background: "#f8fafc",
          }}
        >
          {/* Action buttons */}
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "center",
              marginBottom: total > 0 ? 8 : 0,
            }}
          >
            <button
              type="button"
              data-modal-add
              onClick={onAdd}
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                padding: "4px 14px",
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#334155",
                borderRadius: 4,
                cursor: "pointer",
                minWidth: 62,
              }}
            >
              Add
            </button>

            {editMode ? (
              <button
                type="button"
                data-modal-save
                onClick={onSave}
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  padding: "4px 14px",
                  border: "none",
                  background: "#2563eb",
                  color: "#ffffff",
                  borderRadius: 4,
                  cursor: "pointer",
                  minWidth: 62,
                  boxShadow: "0 1px 3px rgba(37, 99, 235, 0.3)",
                }}
              >
                Save
              </button>
            ) : (
              <button
                type="button"
                data-modal-edit
                onClick={() => setEditMode(true)}
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  padding: "4px 14px",
                  border: "none",
                  background: "#f59e0b",
                  color: "#ffffff",
                  borderRadius: 4,
                  cursor: "pointer",
                  minWidth: 62,
                  boxShadow: "0 1px 3px rgba(245, 158, 11, 0.3)",
                }}
              >
                Edit
              </button>
            )}

            <button
              type="button"
              data-modal-cancel
              onClick={onCancel || onClose}
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                padding: "4px 14px",
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#64748b",
                borderRadius: 4,
                cursor: "pointer",
                minWidth: 62,
              }}
            >
              Cancel
            </button>

            <button
              type="button"
              data-modal-exit
              onClick={onClose}
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                padding: "4px 14px",
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#64748b",
                borderRadius: 4,
                cursor: "pointer",
                minWidth: 62,
              }}
            >
              Exit
            </button>
          </div>

          {/* Record navigation */}
          {total > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              <button
                type="button"
                onClick={() => onNavigate && onNavigate(0)}
                disabled={currentIndex === 0}
                style={{
                  fontSize: 11,
                  padding: "2px 8px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: currentIndex === 0 ? "#94a3b8" : "#334155",
                  cursor: currentIndex === 0 ? "default" : "pointer",
                  borderRadius: 4,
                }}
              >
                |◄
              </button>

              <button
                type="button"
                onClick={() => onNavigate && onNavigate(currentIndex - 1)}
                disabled={currentIndex === 0}
                style={{
                  fontSize: 11,
                  padding: "2px 8px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: currentIndex === 0 ? "#94a3b8" : "#334155",
                  cursor: currentIndex === 0 ? "default" : "pointer",
                  borderRadius: 4,
                }}
              >
                ◄
              </button>

              <span
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  padding: "3px 12px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: "#1e293b",
                  minWidth: 140,
                  textAlign: "center",
                  borderRadius: 4,
                }}
              >
                {currentDate ? formatDate(currentDate) : `Record #${currentIndex + 1}`}
              </span>

              <button
                type="button"
                onClick={() => onNavigate && onNavigate(currentIndex + 1)}
                disabled={currentIndex === total - 1}
                style={{
                  fontSize: 11,
                  padding: "2px 8px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: currentIndex === total - 1 ? "#94a3b8" : "#334155",
                  cursor: currentIndex === total - 1 ? "default" : "pointer",
                  borderRadius: 4,
                }}
              >
                ►
              </button>

              <button
                type="button"
                onClick={() => onNavigate && onNavigate(total - 1)}
                disabled={currentIndex === total - 1}
                style={{
                  fontSize: 11,
                  padding: "2px 8px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: currentIndex === total - 1 ? "#94a3b8" : "#334155",
                  cursor: currentIndex === total - 1 ? "default" : "pointer",
                  borderRadius: 4,
                }}
              >
                ►|
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Input style helper
const inputStyle = (ro, width = 125) => ({
  width: width,
  height: 27,
  fontSize: 11.5,
  padding: "2px 8px",
  border: "1px solid #cbd5e1",
  borderRadius: 4,
  background: ro ? "#f8fafc" : "#ffffff",
  color: "#1e293b",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
});

// Form row helper
const FormRow = ({ label, labelWidth = 200, children }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      marginBottom: 6,
    }}
  >
    <label
      style={{
        width: labelWidth,
        textAlign: "right",
        paddingRight: 10,
        fontWeight: 600,
        color: "#334155",
        fontSize: 11.5,
        margin: 0,
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

/* ════════════════════════════════════════════════════════════════════════════
   1. PF RATE CONFIGURATION MODAL
   ════════════════════════════════════════════════════════════════════════════ */
export const PFModal = ({
  isOpen,
  onClose,
  form,
  setField,
  editMode,
  setEditMode,
  records,
  index,
  onNavigate,
  onAdd,
  onSave,
  onCancel,
}) => {
  const ro = !editMode;

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      title="PF Rate Configuration"
      width={530}
      editMode={editMode}
      setEditMode={setEditMode}
      onAdd={onAdd}
      onSave={onSave}
      onCancel={onCancel}
      records={records}
      currentIndex={index}
      onNavigate={onNavigate}
      currentDate={records[index]?.PFDate || form.PFDate}
    >
      {[
        ["DATE", "PFDate", "date"],
        ["RATE", "Rate", "number"],
        ["EMPLOYER RATE", "EmployerRate", "number"],
        ["PENSION RATE", "PensionRate", "number"],
        ["MAX PENSION AMT", "MaxPensionAmt", "number"],
        ["MAX WAGES FOR PF", "MaxWagesForPF", "number"],
        ["MAX WAGES FOR PENSION", "MaxWagesForPension", "number"],
      ].map(([label, key, type]) => (
        <FormRow key={key} label={label}>
          <input
            type={type}
            style={inputStyle(ro, 125)}
            readOnly={ro}
            value={form[key] !== undefined && form[key] !== null ? form[key] : ""}
            onChange={(e) => setField(key, e.target.value)}
          />
        </FormRow>
      ))}

      {/* Admin Charges */}
      <FormRow label="ADMIN CHARGES">
        <input
          type="number"
          style={inputStyle(ro, 80)}
          readOnly={ro}
          value={form.AdminCharges !== undefined && form.AdminCharges !== null ? form.AdminCharges : ""}
          onChange={(e) => setField("AdminCharges", e.target.value)}
        />
        <label
          style={{
            marginLeft: 10,
            marginRight: 6,
            fontWeight: 600,
            color: "#334155",
            fontSize: 11,
          }}
        >
          MIN. AMT
        </label>
        <input
          type="number"
          style={inputStyle(ro, 70)}
          readOnly={ro}
          value={
            form.AdminChargesMinAmount !== undefined && form.AdminChargesMinAmount !== null
              ? form.AdminChargesMinAmount
              : ""
          }
          onChange={(e) => setField("AdminChargesMinAmount", e.target.value)}
        />
      </FormRow>

      {/* A/C 21 With Group Ins */}
      <FormRow label="A/C 21 WITH GROUP INS">
        <input
          type="number"
          style={inputStyle(ro, 80)}
          readOnly={ro}
          value={form.AC21WithGroupIns !== undefined && form.AC21WithGroupIns !== null ? form.AC21WithGroupIns : ""}
          onChange={(e) => setField("AC21WithGroupIns", e.target.value)}
        />
        <label
          style={{
            marginLeft: 10,
            marginRight: 6,
            fontWeight: 600,
            color: "#334155",
            fontSize: 11,
          }}
        >
          MIN. AMT
        </label>
        <input
          type="number"
          style={inputStyle(ro, 70)}
          readOnly={ro}
          value={
            form.AC21WithGroupInsMinAmt !== undefined && form.AC21WithGroupInsMinAmt !== null
              ? form.AC21WithGroupInsMinAmt
              : ""
          }
          onChange={(e) => setField("AC21WithGroupInsMinAmt", e.target.value)}
        />
      </FormRow>

      {/* A/C 22 With Group Ins */}
      <FormRow label="A/C 22 WITH GROUP INS">
        <input
          type="number"
          style={inputStyle(ro, 80)}
          readOnly={ro}
          value={form.AC22WithGroupIns !== undefined && form.AC22WithGroupIns !== null ? form.AC22WithGroupIns : ""}
          onChange={(e) => setField("AC22WithGroupIns", e.target.value)}
        />
        <label
          style={{
            marginLeft: 10,
            marginRight: 6,
            fontWeight: 600,
            color: "#334155",
            fontSize: 11,
          }}
        >
          MIN. AMT
        </label>
        <input
          type="number"
          style={inputStyle(ro, 70)}
          readOnly={ro}
          value={
            form.AC22WithGroupInsMinAmt !== undefined && form.AC22WithGroupInsMinAmt !== null
              ? form.AC22WithGroupInsMinAmt
              : ""
          }
          onChange={(e) => setField("AC22WithGroupInsMinAmt", e.target.value)}
        />
      </FormRow>

      {[
        ["A/C 21 WO GROUP INS", "AC21WithoutGroupIns"],
        ["A/C 22 WO GROUP INS", "AC22WithoutGroupIns"],
        ["HIGHER PENSION RATE", "HigherPensionRate"],
      ].map(([label, key]) => (
        <FormRow key={key} label={label}>
          <input
            type="number"
            style={inputStyle(ro, 125)}
            readOnly={ro}
            value={form[key] !== undefined && form[key] !== null ? form[key] : ""}
            onChange={(e) => setField(key, e.target.value)}
          />
        </FormRow>
      ))}

      {/* Checkboxes */}
      {[
        ["PF BASIS", "PFBasis"],
        ["PENSION ON ARREAR", "PensionOnArrear"],
        ["AC 21 INDIVIDUALLY", "AC21Individually"],
      ].map(([label, key]) => (
        <FormRow key={key} label={label}>
          <input
            type="checkbox"
            disabled={ro}
            checked={!!form[key]}
            onChange={(e) => setField(key, e.target.checked)}
            style={{
              width: 15,
              height: 15,
              cursor: ro ? "default" : "pointer",
              accentColor: "#2563eb",
            }}
          />
        </FormRow>
      ))}

      {/* New Labour Code */}
      {[
        ["NEW LABOUR CODE APPLY DATE :", "NewLabourCodeApplyDate", "date"],
        ["NEW LABOUR CODE BASIC PREC.:", "NewLabourCodeBasicPrec", "number"],
      ].map(([label, key, type]) => (
        <FormRow key={key} label={label}>
          <input
            type={type}
            style={inputStyle(ro, 125)}
            readOnly={ro}
            value={form[key] !== undefined && form[key] !== null ? form[key] : ""}
            onChange={(e) => setField(key, e.target.value)}
          />
        </FormRow>
      ))}
    </ModalContainer>
  );
};

/* ════════════════════════════════════════════════════════════════════════════
   2. ESI RATE CONFIGURATION MODAL
   ════════════════════════════════════════════════════════════════════════════ */
export const ESIModal = ({
  isOpen,
  onClose,
  form,
  setField,
  editMode,
  setEditMode,
  records,
  index,
  onNavigate,
  onAdd,
  onSave,
  onCancel,
}) => {
  const ro = !editMode;

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      title="ESI Rate Configuration"
      width={490}
      editMode={editMode}
      setEditMode={setEditMode}
      onAdd={onAdd}
      onSave={onSave}
      onCancel={onCancel}
      records={records}
      currentIndex={index}
      onNavigate={onNavigate}
      currentDate={records[index]?.ESIDate || form.ESIDate}
    >
      <FormRow label="W.E.F. DATE :" labelWidth={215}>
        <input
          type="date"
          style={inputStyle(ro, 130)}
          readOnly={ro}
          value={form.ESIDate || ""}
          onChange={(e) => setField("ESIDate", e.target.value)}
        />
      </FormRow>

      {[
        ["EMPLOYEE  CONTRIBUTION :", "EmployeeContribution"],
        ["EMPLOYER CONTRIBUTION:", "EmployerContribution"],
        ["WAGES LIMIT :", "WagesLimit"],
      ].map(([label, key]) => (
        <FormRow key={key} label={label} labelWidth={215}>
          <input
            type="number"
            style={inputStyle(ro, 130)}
            readOnly={ro}
            value={form[key] !== undefined && form[key] !== null ? form[key] : ""}
            onChange={(e) => setField(key, e.target.value)}
          />
        </FormRow>
      ))}

      {[
        ["ROUNDED ON :", "RoundedOn"],
        ["EMPLOYER PART INDIVIDUALLY :", "EmployerPartIndividually"],
      ].map(([label, key]) => (
        <FormRow key={key} label={label} labelWidth={215}>
          <input
            type="checkbox"
            disabled={ro}
            checked={!!form[key]}
            onChange={(e) => setField(key, e.target.checked)}
            style={{
              width: 15,
              height: 15,
              cursor: ro ? "default" : "pointer",
              accentColor: "#2563eb",
            }}
          />
        </FormRow>
      ))}

      <FormRow label="EMPLOYEE EXEMPTION LIMIT :" labelWidth={215}>
        <input
          type="number"
          style={inputStyle(ro, 130)}
          readOnly={ro}
          value={
            form.EmployeeExemptionLimit !== undefined && form.EmployeeExemptionLimit !== null
              ? form.EmployeeExemptionLimit
              : ""
          }
          onChange={(e) => setField("EmployeeExemptionLimit", e.target.value)}
        />
      </FormRow>

      <FormRow label="RURAL :" labelWidth={215}>
        <input
          type="checkbox"
          disabled={ro}
          checked={!!form.Rural}
          onChange={(e) => setField("Rural", e.target.checked)}
          style={{
            width: 15,
            height: 15,
            cursor: ro ? "default" : "pointer",
            accentColor: "#2563eb",
          }}
        />
      </FormRow>

      {[
        ["NEW LABOUR CODE APPLY DATE :", "NewLabourCodeApplyDate", "date"],
        ["NEW LABOUR CODE BASIC PREC.:", "NewLabourCodeBasicPrec", "number"],
      ].map(([label, key, type]) => (
        <FormRow key={key} label={label} labelWidth={215}>
          <input
            type={type}
            style={inputStyle(ro, 130)}
            readOnly={ro}
            value={form[key] !== undefined && form[key] !== null ? form[key] : ""}
            onChange={(e) => setField(key, e.target.value)}
          />
        </FormRow>
      ))}
    </ModalContainer>
  );
};

/* ════════════════════════════════════════════════════════════════════════════
   3. BONUS PARAMETERS MODAL
   ════════════════════════════════════════════════════════════════════════════ */
export const BonusModal = ({
  isOpen,
  onClose,
  form,
  setField,
  editMode,
  setEditMode,
  records,
  index,
  onNavigate,
  onAdd,
  onSave,
  onCancel,
}) => {
  const ro = !editMode;

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      title="Bonus Parameters"
      width={490}
      editMode={editMode}
      setEditMode={setEditMode}
      onAdd={onAdd}
      onSave={onSave}
      onCancel={onCancel}
      records={records}
      currentIndex={index}
      onNavigate={onNavigate}
      currentDate={records[index]?.BonusDate || form.BonusDate}
    >
      <FormRow label="W.E.F. DATE :" labelWidth={215}>
        <input
          type="date"
          style={inputStyle(ro, 130)}
          readOnly={ro}
          value={form.BonusDate || ""}
          onChange={(e) => setField("BonusDate", e.target.value)}
        />
      </FormRow>

      {[
        ["BONUS RATE :", "BonusRate"],
        ["MAXIMUM WAGES:", "MaximumWages"],
        ["MAXIMUM WAGES LIMIT :", "MaximumWagesLimit"],
        ["MAXIMUM BONUS :", "MaximumBonus"],
        ["MINIMUM BONUS :", "MinimumBonus"],
        ["MINIMUM BONUS DAYS :", "MinimumBonusDays"],
      ].map(([label, key]) => (
        <FormRow key={key} label={label} labelWidth={215}>
          <input
            type="number"
            style={inputStyle(ro, 130)}
            readOnly={ro}
            value={form[key] !== undefined && form[key] !== null ? form[key] : ""}
            onChange={(e) => setField(key, e.target.value)}
          />
        </FormRow>
      ))}
    </ModalContainer>
  );
};

/* ════════════════════════════════════════════════════════════════════════════
   4. GRATUITY RATE CONFIGURATION MODAL
   ════════════════════════════════════════════════════════════════════════════ */
export const GratuityModal = ({
  isOpen,
  onClose,
  form,
  setField,
  editMode,
  setEditMode,
  records,
  index,
  onNavigate,
  onAdd,
  onSave,
  onCancel,
}) => {
  const ro = !editMode;

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      title="Gratuity Rate Configuration"
      width={450}
      editMode={editMode}
      setEditMode={setEditMode}
      onAdd={onAdd}
      onSave={onSave}
      onCancel={onCancel}
      records={records}
      currentIndex={index}
      onNavigate={onNavigate}
      currentDate={records[index]?.GratuityDate || form.GratuityDate}
    >
      <FormRow label="DATE :" labelWidth={140}>
        <input
          type="date"
          style={inputStyle(ro, 140)}
          readOnly={ro}
          value={form.GratuityDate || ""}
          onChange={(e) => setField("GratuityDate", e.target.value)}
        />
      </FormRow>

      <FormRow label="RATE :" labelWidth={140}>
        <input
          type="number"
          style={inputStyle(ro, 140)}
          readOnly={ro}
          value={form.Rate !== undefined && form.Rate !== null ? form.Rate : ""}
          onChange={(e) => setField("Rate", e.target.value)}
        />
      </FormRow>
    </ModalContainer>
  );
};

/* ════════════════════════════════════════════════════════════════════════════
   5. GROUP INSURANCE CONFIGURATION MODAL
   ════════════════════════════════════════════════════════════════════════════ */
export const GIModal = ({
  isOpen,
  onClose,
  form,
  setField,
  editMode,
  setEditMode,
  records,
  index,
  onNavigate,
  onAdd,
  onSave,
  onCancel,
}) => {
  const ro = !editMode;

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      title="Group Insurance Configuration"
      width={450}
      editMode={editMode}
      setEditMode={setEditMode}
      onAdd={onAdd}
      onSave={onSave}
      onCancel={onCancel}
      records={records}
      currentIndex={index}
      onNavigate={onNavigate}
      currentDate={records[index]?.GIDate || form.GIDate}
    >
      <div
        style={{
          border: "1px solid #e2e8f0",
          padding: "14px 16px",
          background: "#f8fafc",
          borderRadius: 6,
          marginBottom: 6,
        }}
      >
        <FormRow label="W.E.F. DATE :" labelWidth={150}>
          <input
            type="date"
            style={inputStyle(ro, 140)}
            readOnly={ro}
            value={form.GIDate || ""}
            onChange={(e) => setField("GIDate", e.target.value)}
          />
        </FormRow>

        <FormRow label="Group Insurance :" labelWidth={150}>
          <input
            type="checkbox"
            disabled={ro}
            checked={!!form.GroupInsurance}
            onChange={(e) => setField("GroupInsurance", e.target.checked)}
            style={{
              width: 16,
              height: 16,
              cursor: ro ? "default" : "pointer",
              accentColor: "#2563eb",
            }}
          />
        </FormRow>
      </div>
    </ModalContainer>
  );
};

/* ════════════════════════════════════════════════════════════════════════════
   6. EMPLOYEE RATES (BIDI/LIME STONE) MODAL
   ════════════════════════════════════════════════════════════════════════════ */
export const BidiModal = ({
  isOpen,
  onClose,
  form,
  setField,
  editMode,
  setEditMode,
  records,
  index,
  onNavigate,
  onAdd,
  onSave,
  onCancel,
}) => {
  const ro = !editMode;

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      title="Employee Rates (Bidi/Lime Stone)"
      width={450}
      editMode={editMode}
      setEditMode={setEditMode}
      onAdd={onAdd}
      onSave={onSave}
      onCancel={onCancel}
      records={records}
      currentIndex={index}
      onNavigate={onNavigate}
      currentDate={records[index]?.BidiDate || form.BidiDate}
    >
      <div
        style={{
          border: "1px solid #e2e8f0",
          padding: "14px 16px",
          background: "#f8fafc",
          borderRadius: 6,
          marginBottom: 6,
        }}
      >
        <FormRow label="W.E.F. DATE :" labelWidth={150}>
          <input
            type="date"
            style={inputStyle(ro, 140)}
            readOnly={ro}
            value={form.BidiDate || ""}
            onChange={(e) => setField("BidiDate", e.target.value)}
          />
        </FormRow>

        <FormRow label="TON/BIDI RATE :" labelWidth={150}>
          <input
            type="number"
            style={inputStyle(ro, 100)}
            readOnly={ro}
            value={form.TonBidiRate !== undefined && form.TonBidiRate !== null ? form.TonBidiRate : ""}
            onChange={(e) => setField("TonBidiRate", e.target.value)}
          />
        </FormRow>
      </div>
    </ModalContainer>
  );
};
